import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApplyCarePathway } from '../state/data/pathway';
import SubtrackPicker from './subtrack/SubtrackPicker';
import { hasDiagnosisChange } from '../logic/pathwayConditions';

/**
 * Applies the care pathway matching the patient's resolved ACCESS subtrack
 * (or a care-team override) to their care plan via WF36 → WF15.
 * onSuccess({confirmed}): confirmed=false means dismissed after an
 * unconfirmed WF15 poll timeout, not a failure.
 */
export default function PathwayPresetModal({ patient, carePlan, currentUser, onClose, onSuccess }) {
  const applyMut = useApplyCarePathway();
  const submitting = applyMut.isPending;
  const [sel, setSel] = useState(null);
  const [error, setError] = useState(null);
  // Apply fired + 30s poll elapsed without the plan write landing — the workflow
  // may still land later, so this is a warning state, not a failure.
  const [unconfirmed, setUnconfirmed] = useState(false);

  const onSelectionChange = useCallback((s) => setSel(s), []);

  // After an unconfirmed apply, every way out routes through onSuccess so the
  // shell refetches and toasts honestly instead of silently closing.
  const handleDismiss = useCallback(() => {
    if (submitting) return;
    if (unconfirmed) { onSuccess?.({ confirmed: false, plan: null }); return; }
    onClose();
  }, [submitting, unconfirmed, onClose, onSuccess]);

  // Escape must route through handleDismiss too — CAPTURE + stopPropagation, or the
  // drawer's window-level Escape listener unmounts the modal mid-poll and the
  // confirmation callback is lost (see the modal pattern in .claude/rules/app.md).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      handleDismiss();
    };
    window.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [handleDismiss]);

  function handleApply() {
    if (!sel?.selectedId) { setError('Select a subtrack pathway.'); return; }
    setError(null);
    setUnconfirmed(false);
    const approvedBy = currentUser
      ? [currentUser.firstname, currentUser.lastname].filter(Boolean).join(' ') || currentUser.accountid || null
      : null;
    applyMut.mutate(
      {
        withingsUserId: patient.userid,
        selected: [sel.selectedId],
        ckdStage: sel.ckdStage,
        approvedBy,
        // The diagnosis shown in the picker's preview — Apply is the attestation.
        confirmedConditions: sel.diagnosisDelta?.proposed ?? null,
      },
      {
        onSuccess: (outcome) => {
          if (outcome?.confirmed) onSuccess?.(outcome);
          else setUnconfirmed(true);
        },
        onError: (err) => setError(err.message || 'Failed to apply pathway'),
      },
    );
  }

  const patientName = [patient.first_name, patient.last_name].filter(Boolean).join(' ').trim();
  const applyDisabled = submitting || !sel?.selectedId || !sel?.preview;
  const diagnosisChanges = hasDiagnosisChange(sel?.diagnosisDelta ?? null);

  return createPortal((
    <div
      onClick={handleDismiss}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--wi-background-secondary)', borderRadius: 14,
          width: 880, maxWidth: '94vw', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
          fontFamily: 'var(--wi-font-family)',
        }}
      >
        {/* ── HEADER (fixed) ── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--wi-divider)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 500, letterSpacing: '-0.18px' }}>
              Apply care pathway
            </h3>
            <span style={{
              background: 'var(--wi-background-primary)',
              borderRadius: 20, padding: '4px 10px', fontSize: 12,
              color: 'var(--wi-text-secondary)', whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 340,
            }}>
              {patientName ? `${patientName} · ${patient.userid}` : patient.userid}
            </span>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Close"
            disabled={submitting}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--wi-background-primary)',
              color: 'var(--wi-text-secondary)',
              border: 'none', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 16, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, fontFamily: 'var(--wi-font-family)',
            }}
          >×</button>
        </div>

        {/* ── BODY (scrolls) ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', background: 'var(--wi-background-primary)' }}>
          <SubtrackPicker key={patient.userid} patient={patient} carePlan={carePlan} onSelectionChange={onSelectionChange} />
        </div>

        {/* ── FOOTER (fixed) ── */}
        <div style={{
          borderTop: '1px solid var(--wi-divider)',
          padding: '14px 24px',
          background: 'var(--wi-background-secondary)',
          flexShrink: 0,
        }}>
          {error && (
            <div style={{
              background: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)',
              padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13,
            }}>{error}</div>
          )}
          {submitting && (
            <div style={{ color: 'var(--wi-text-secondary)', marginBottom: 10, fontSize: 13 }}>
              Waiting for the change to be confirmed — this can take up to 30 seconds.
            </div>
          )}
          {unconfirmed && (
            <div style={{
              background: 'var(--wi-status-moderate-10)', color: 'var(--wi-text-primary)',
              padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13,
            }}>
              The change was submitted but hasn&apos;t been confirmed yet — it may still be
              processing. Close to refresh, and check the care plan again in a moment.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
            {unconfirmed ? (
              <button onClick={handleDismiss} className="btn-primary" style={{ cursor: 'pointer' }}>
                Close
              </button>
            ) : (
              <>
                <button
                  onClick={handleDismiss}
                  disabled={submitting}
                  className="btn-ok"
                  style={{ cursor: submitting ? 'not-allowed' : 'pointer' }}
                >Cancel</button>
                <button
                  onClick={handleApply}
                  disabled={applyDisabled}
                  className="btn-primary"
                  style={{
                    cursor: applyDisabled ? 'not-allowed' : 'pointer',
                    opacity: applyDisabled ? 0.55 : 1,
                  }}
                >
                  {submitting ? 'Applying…' : diagnosisChanges ? 'Apply pathway & update diagnosis' : 'Apply pathway'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
